import { useRef, useEffect, useState } from "react";
import { Crosshair, Palette, Award, Sparkles, ArrowRight } from "lucide-react";

const values = [
  {
    icon: Crosshair,
    title: "Précision chirurgicale",
    description: "Notre IA comprend vos intentions. Décrivez avec vos mots, obtenez exactement ce que vous imaginez.",
    gradient: "from-rose-500 to-pink-500",
    stat: "99.2%",
    statLabel: "Satisfaction"
  },
  {
    icon: Palette,
    title: "Simple comme bonjour",
    description: "Pas besoin de compétences en design. Tapez, cliquez, téléchargez. Vos visuels sont prêts en 10 secondes.",
    gradient: "from-blue-500 to-cyan-500",
    stat: "< 30s",
    statLabel: "Prise en main"
  },
  {
    icon: Award,
    title: "Qualité studio",
    description: "Des visuels HD et 4K dignes des meilleures agences créatives. Impressionnez vos clients sans effort.",
    gradient: "from-violet-500 to-purple-500",
    stat: "4K",
    statLabel: "Ultra HD"
  },
];

export const ValueSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 bg-card/30 relative overflow-hidden">
      {/* Animated gradient lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      {/* Background orbs */}
      <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2" />
      <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl -translate-y-1/2" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <span 
            className={`inline-flex items-center gap-2 text-sm font-medium text-primary mb-4 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Pourquoi Floop ?
          </span>
          <h2 
            className={`text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 font-display transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            La création visuelle{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">réinventée</span>
          </h2>
          <p 
            className={`text-lg text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Fini les heures passées sur Photoshop. Floop génère des visuels professionnels pendant que vous prenez votre café.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {values.map((value, index) => (
            <div
              key={index}
              className={`group relative transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Card glow effect */}
              <div 
                className={`absolute -inset-0.5 bg-gradient-to-r ${value.gradient} rounded-3xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500`}
              />
              
              {/* Card */}
              <div className="relative h-full bg-card/80 backdrop-blur-xl border border-border/50 rounded-3xl p-8 hover:border-primary/30 transition-all duration-500 overflow-hidden">
                {/* Background pattern on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500"
                  style={{
                    backgroundImage: `radial-gradient(circle at 50% 50%, hsl(var(--primary)) 1px, transparent 1px)`,
                    backgroundSize: "20px 20px"
                  }}
                />
                
                {/* Icon with animated background */}
                <div className="relative mb-6">
                  <div className={`absolute inset-0 bg-gradient-to-r ${value.gradient} rounded-2xl blur-xl opacity-0 group-hover:opacity-40 transition-opacity duration-500`} />
                  <div className="relative w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="w-8 h-8 text-primary" />
                  </div>
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {value.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {value.description}
                </p>
                
                {/* Stat badge */}
                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                  <div>
                    <div className={`text-2xl font-bold bg-gradient-to-r ${value.gradient} bg-clip-text text-transparent`}>
                      {value.stat}
                    </div>
                    <div className="text-xs text-muted-foreground">{value.statLabel}</div>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary group-hover:translate-x-1 transition-all duration-300">
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-white transition-colors" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
